// Siege panel (Phase 5). Two contexts:
//   • army context — the selected army stands on an enemy stronghold: begin/run a siege,
//     set the disposition, hire specialists, storm the place.
//   • world context — a roster of every known stronghold and live siege.
//
// The whole rules model lives server-side; this panel renders what /sieges returns and posts
// intent back. The one bit of local arithmetic is the disposition preview, so the sliders feel
// live without a round trip per drag.
import React, { useEffect, useState, useCallback } from 'react'
import { C, ui } from '../theme'
// Reads only: every mutation is handed down as a callback by Campaign.js, which owns the
// refresh. (Do not import setSpecialists/setSiegeAllocation here — the names would collide
// with this component's own state setters.)
import { getSiegeAllocation, getSpecialists, previewSurrenderCheck } from '../api'

const h = ui.h, btn = ui.btn, btnGhost = ui.btnGhost, input = ui.input

// The Rules' progress ladder. Ratios are of the target's defensive value.
const MILESTONES = [
  { key: 'fortified_camp', pct: 0.5, label: 'Fortified camp', effect: 'Loose blockade possible' },
  { key: 'exit_works', pct: 1.5, label: 'Works at the exits', effect: 'Partial blockade; +1 surrender' },
  { key: 'artillery', pct: 3, label: 'Artillery prepared', effect: '+0.05 assault; +2 surrender' },
  { key: 'circumvallation', pct: 5, label: 'Circumvallation', effect: 'Tight blockade; +0.10 assault; +3 surrender' },
  { key: 'contravallation', pct: 7, label: 'Contravallation', effect: 'Full blockade; +0.15 assault; +4 surrender' },
  { key: 'approaches', pct: 10, label: 'Approaches complete', effect: 'Always ≥ partial; +0.30 assault; +6 surrender' },
]
const ROLES = [
  ['laborers', 'Laborers', 'dig the works — this is what moves the siege forward'],
  ['labor_guards', 'Labor guards', 'protect the laborers from sallies'],
  ['works_guards', 'Works guards', 'man the blockade and defend the camp'],
  ['supply_troops', 'Supply troops', 'escort caravans and forage'],
  ['scouts', 'Scouts', 'warn of a relieving army in time to react'],
]
const BLOCKADE_LABEL = { none: 'None', loose: 'Loose', partial: 'Partial', tight: 'Tight', full: 'Full' }
const BLOCKADE_EFFECT = {
  none: 'stores untouched; the target can still stockpile',
  loose: 'stockpiling stops, but stores hold',
  partial: 'stores drain at 50% · +2 surrender',
  tight: 'stores drain at 75% · +4 surrender · no escape without a fight',
  full: 'stores drain at full speed · +6 surrender',
}
const num = (n, d = 0) => n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d })
const pct = (n) => `${Math.round((n || 0) * 100) / 100}%`

// ── the milestone ladder, drawn as a single bar with notches ────────────────────────────
function ProgressLadder({ siege }) {
  const p = siege.progressPct || 0
  const scaleMax = Math.max(12, Math.ceil(p / 2) * 2)   // grows past 10% for assault prep
  return (
    <div style={{ margin: '4px 0 10px' }}>
      <div style={{ position: 'relative', height: 16, background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, width: `${Math.min(100, p / scaleMax * 100)}%`, background: C.gold, opacity: 0.75 }} />
        {MILESTONES.map(m => (
          <div key={m.key} title={`${m.label} — ${m.pct}%`}
            style={{
              position: 'absolute', top: 0, bottom: 0, left: `${m.pct / scaleMax * 100}%`, width: 1,
              background: (siege.reached || []).includes(m.key) ? C.paperRaised : C.ruleStrong, opacity: 0.9,
            }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: C.inkSoft, marginTop: 3 }}>
        <span className="tnum">{pct(p)} of {num(siege.defensive_value)} gp</span>
        <span className="tnum">{num(siege.labor_total)} labor</span>
      </div>
    </div>
  )
}

function MilestoneList({ siege }) {
  const reached = siege.reached || []
  return (
    <div style={{ fontSize: 11.5 }}>
      {MILESTONES.map(m => {
        const done = reached.includes(m.key)
        const next = !done && !MILESTONES.slice(0, MILESTONES.indexOf(m)).some(x => !reached.includes(x.key))
        return (
          <div key={m.key} style={{
            display: 'flex', gap: 7, padding: '3px 0', alignItems: 'baseline',
            color: done ? C.ink : (next ? C.inkSoft : C.inkFaint),
          }}>
            <span style={{ width: 12 }}>{done ? '✓' : (next ? '▸' : '·')}</span>
            <span className="tnum" style={{ width: 34, textAlign: 'right' }}>{m.pct}%</span>
            <span style={{ flex: 1, fontWeight: done ? 600 : 400 }}>
              {m.label}
              <span style={{ display: 'block', fontSize: 10.5, color: C.inkFaint }}>{m.effect}</span>
            </span>
          </div>
        )
      })}
      {siege.assaultPrepLevels > 0 && (
        <div style={{ display: 'flex', gap: 7, padding: '3px 0', color: C.ink }}>
          <span style={{ width: 12 }}>✓</span>
          <span className="tnum" style={{ width: 34, textAlign: 'right' }}>+{siege.assaultPrepLevels * 2}%</span>
          <span style={{ flex: 1, fontWeight: 600 }}>
            Assault preparation ×{siege.assaultPrepLevels}
            <span style={{ display: 'block', fontSize: 10.5, color: C.inkFaint }}>
              +{(siege.assaultPrepLevels * 0.05).toFixed(2)} assault · +{siege.assaultPrepLevels * 2} surrender
            </span>
          </span>
        </div>
      )}
    </div>
  )
}

// ── the native militia, always shown apart from any field army ──────────────────────────
function GarrisonCard({ garrison, strongholdType }) {
  if (!garrison) return null
  if (strongholdType === 'fortress') {
    return (
      <div style={{ fontSize: 11.5, color: C.inkSoft, padding: '6px 8px', background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 4 }}>
        <b style={{ color: C.ink }}>No native garrison.</b> A fortress's people live in the villages
        around it — only a field army defends these walls.
      </div>
    )
  }
  const full = garrison.fullStrength || 0
  const men = garrison.men || 0
  return (
    <div style={{ padding: '6px 8px', background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
        <b>Native militia</b>
        <span className="tnum">{num(men)} / {num(full)}</span>
      </div>
      <div style={{ height: 6, background: C.paper, border: `1px solid ${C.rule}`, borderRadius: 2, margin: '4px 0', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${full ? Math.min(100, men / full * 100) : 0}%`, background: C.crimson, opacity: 0.7 }} />
      </div>
      <div style={{ fontSize: 10.5, color: C.inkSoft }}>
        {garrison.troop} · {num(garrison.wageGp, 2)} gp/day of defence
        {garrison.note
          ? <span style={{ display: 'block', color: C.crimson }}>{garrison.note}</span>
          : <span style={{ display: 'block' }}>
              {garrison.regenPaused
                ? 'No replenishment while the siege lasts.'
                : `Replenishing ${num(garrison.regenPerDay)} men/day (2% of full strength).`}
            </span>}
      </div>
    </div>
  )
}

// ── disposition: % sliders, with an optional per-pool override ──────────────────────────
function Disposition({ army, allocation, onSave, busy }) {
  const [pctDraft, setPctDraft] = useState(null)
  const [advanced, setAdvanced] = useState(false)
  const [byPool, setByPool] = useState({})

  useEffect(() => {
    if (!allocation) return
    setPctDraft(allocation.pct || allocation.defaultPct)
    setByPool(prev => (Object.keys(prev).length ? prev : {}))
  }, [allocation])

  if (!allocation || !pctDraft) return <div style={{ fontSize: 12, color: C.inkSoft }}>Loading disposition…</div>

  const pools = (army.pools || []).filter(p => !p.disbanded)
  const totalMen = pools.reduce((s, p) => s + (p.men || 0), 0)
  const totalPct = ROLES.reduce((s, [k]) => s + (Number(pctDraft[k]) || 0), 0)
  const over = totalPct > 100

  // live preview without a round trip: the server floors per pool, so mirror that
  const previewMen = (role) => pools.reduce((s, p) => s + Math.floor((p.men || 0) * (Number(pctDraft[role]) || 0) / 100), 0)
  const assigned = ROLES.reduce((s, [k]) => s + previewMen(k), 0)

  const save = () => {
    const body = { pct: pctDraft }
    if (advanced && Object.keys(byPool).length) body.byPool = byPool
    onSave(body)
  }

  return (
    <>
      <div style={h}>Disposition</div>
      <div style={{ fontSize: 11.5, color: C.inkSoft, marginBottom: 8 }}>
        How this army's {num(totalMen)} men spend the day. Anything unassigned sits in camp as a
        reserve — still available if you storm the place.
      </div>
      {ROLES.map(([key, label, hint]) => (
        <div key={key} style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span title={hint}>{label}</span>
            <span className="tnum" style={{ color: C.inkSoft }}>
              {Number(pctDraft[key]) || 0}% · {num(previewMen(key))} men
            </span>
          </div>
          <input type="range" min={0} max={100} step={5} value={Number(pctDraft[key]) || 0}
            onChange={(e) => setPctDraft({ ...pctDraft, [key]: Number(e.target.value) })}
            style={{ width: '100%', accentColor: C.gold }} />
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: over ? C.crimson : C.inkSoft }}>
        <span>{totalPct}% assigned{over ? ' — over 100%' : ''}</span>
        <span className="tnum">{num(totalMen - assigned)} in reserve</span>
      </div>

      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11.5, margin: '8px 0', cursor: 'pointer' }}>
        <input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} />
        Assign specific units instead (advanced)
      </label>
      {advanced && (
        <div style={{ border: `1px solid ${C.rule}`, borderRadius: 4, padding: 8, marginBottom: 8, background: C.paperSunken }}>
          <div style={{ fontSize: 11, color: C.inkSoft, marginBottom: 6 }}>
            Any unit given explicit numbers here ignores the sliders entirely. Useful for pinning
            casters to the works — only wizards, sorcerers and druids dig at 25 labor a head.
          </div>
          {pools.map(p => (
            <div key={p.id} style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600 }}>{p.troop_name} <span style={{ color: C.inkSoft, fontWeight: 400 }}>({num(p.men)} men)</span></div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
                {ROLES.map(([key, label]) => (
                  <input key={key} type="number" min={0} max={p.men} placeholder={label.split(' ')[0]}
                    title={label}
                    value={(byPool[p.id] || {})[key] ?? ''}
                    onChange={(e) => setByPool({
                      ...byPool,
                      [p.id]: { ...(byPool[p.id] || {}), [key]: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value)) },
                    })}
                    style={{ ...input, width: 58, marginBottom: 0, padding: '4px 5px', fontSize: 11 }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <button style={{ ...btn, opacity: over || busy ? 0.5 : 1 }} disabled={over || busy} onClick={save}>
        {busy ? 'Saving…' : 'Save disposition'}
      </button>
      {allocation.laborForce && (
        <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 6 }}>
          Currently digging: {num(allocation.laborForce.laborerMen)} men
          {allocation.laborForce.casters && allocation.laborForce.casters.length
            ? ` + ${num(allocation.laborForce.casters.reduce((s, c) => s + c.men, 0))} casters` : ''}
        </div>
      )}
    </>
  )
}

// ── engineers & medics ──────────────────────────────────────────────────────────────────
function Specialists({ specialists, onSave, busy }) {
  const [draft, setDraft] = useState(null)
  useEffect(() => {
    if (!specialists) return
    const byType = Object.fromEntries(specialists.map(s => [s.type, s.count]))
    setDraft({ engineer: byType.engineer || 0, medic: byType.medic || 0 })
  }, [specialists])
  if (!draft) return null
  const cost = draft.engineer * 10 + draft.medic * 3
  return (
    <>
      <div style={h}>Specialists</div>
      <div style={{ fontSize: 11.5, color: C.inkSoft, marginBottom: 8 }}>
        Each engineer directs 200 laborers; men beyond that work at a quarter pace. Fewer than
        five engineers also caps how far the works can ever be pushed.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ flex: 1, fontSize: 12 }}>
          Engineers <span style={{ color: C.inkSoft }}>(10 gp/day)</span>
          <input type="number" min={0} value={draft.engineer}
            onChange={(e) => setDraft({ ...draft, engineer: Math.max(0, Number(e.target.value) || 0) })} style={input} />
        </label>
        <label style={{ flex: 1, fontSize: 12 }}>
          Medics <span style={{ color: C.inkSoft }}>(3 gp/day)</span>
          <input type="number" min={0} value={draft.medic}
            onChange={(e) => setDraft({ ...draft, medic: Math.max(0, Number(e.target.value) || 0) })} style={input} />
        </label>
      </div>
      <div style={{ fontSize: 11.5, color: C.inkSoft, marginBottom: 6 }}>
        <span className="tnum">{num(cost, 2)} gp/day</span> upkeep.
        {draft.engineer > 0 && draft.engineer < 5 && (
          <span style={{ color: C.crimson }}> Under five engineers: works capped, labor ×
            {[0.1, 0.3, 0.5, 0.7, 0.85][draft.engineer]}.</span>
        )}
        {draft.medic > 0 && <span> Medics stabilize {draft.medic * 2} casualties per battle.</span>}
      </div>
      <button style={{ ...btn, ...(busy ? { opacity: 0.5 } : {}) }} disabled={busy}
        onClick={() => onSave(draft)}>{busy ? 'Saving…' : 'Hire / dismiss'}</button>
    </>
  )
}

// ── the DM's surrender confirmation ─────────────────────────────────────────────────────
function SurrenderCheck({ siege, onRoll, busy }) {
  const [q, setQ] = useState({ relief: 'none', failedRelief: false, failedSallies: 0, rollsSinceApproaches: 0 })
  const [preview, setPreview] = useState(null)
  const load = useCallback(() => {
    previewSurrenderCheck(siege.id, {
      relief: q.relief, failedRelief: q.failedRelief ? 'true' : '',
      failedSallies: q.failedSallies, rollsSinceApproaches: q.rollsSinceApproaches,
    }).then(setPreview).catch(() => {})
  }, [siege.id, q])
  useEffect(() => { load() }, [load])

  return (
    <>
      <div style={h}>Surrender check</div>
      {siege.stronghold_type === 'fortress' ? (
        <div style={{ fontSize: 11.5, color: C.inkSoft, marginBottom: 8 }}>
          A fortress does not surrender to this roll — it yields at its leader's option, or on a
          starvation roll you judge. The breakdown below is still the pressure it is under.
        </div>
      ) : (
        <div style={{ fontSize: 11.5, color: C.inkSoft, marginBottom: 8 }}>
          d20 every 5 days; the city surrenders on a modified 30 or better.
        </div>
      )}
      <label style={{ fontSize: 12, display: 'block' }}>
        Expected relief <span style={{ color: C.inkFaint }}>(your judgement)</span>
        <select value={q.relief} onChange={(e) => setQ({ ...q, relief: e.target.value })} style={input}>
          <option value="none">No relief expected</option>
          <option value="in_field">A relief army is in the field (−3)</option>
          <option value="approaching">Known to be approaching (−6)</option>
          <option value="imminent">Arriving within 5 days (−10)</option>
        </select>
      </label>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
        <input type="checkbox" checked={q.failedRelief} onChange={(e) => setQ({ ...q, failedRelief: e.target.checked })} />
        A relief attempt has already failed (+8)
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ flex: 1, fontSize: 12 }}>Costly failed sallies (+2 ea.)
          <input type="number" min={0} value={q.failedSallies}
            onChange={(e) => setQ({ ...q, failedSallies: Math.max(0, Number(e.target.value) || 0) })} style={input} />
        </label>
        <label style={{ flex: 1, fontSize: 12 }}>Rolls since approaches (+2 ea.)
          <input type="number" min={0} value={q.rollsSinceApproaches}
            onChange={(e) => setQ({ ...q, rollsSinceApproaches: Math.max(0, Number(e.target.value) || 0) })} style={input} />
        </label>
      </div>

      {preview && (
        <div style={{ background: C.paperSunken, border: `1px solid ${C.rule}`, borderRadius: 4, padding: 8, margin: '6px 0', fontSize: 11.5 }}>
          {(preview.parts || []).map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span style={{ color: p.dmJudgement ? C.gold : C.inkSoft }}>
                {p.label}{p.dmJudgement ? ' *' : ''}
              </span>
              <span className="tnum" style={{ color: p.value > 0 ? C.green : C.crimson }}>
                {p.value > 0 ? '+' : ''}{p.value}
              </span>
            </div>
          ))}
          <div style={{ borderTop: `1px solid ${C.rule}`, marginTop: 5, paddingTop: 5, display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
            <span>Total {preview.total >= 0 ? '+' : ''}{preview.total} · needs</span>
            <span className="tnum">
              {preview.needsOnD20 > 20 ? 'impossible this roll' : preview.needsOnD20 <= 1 ? 'automatic' : `${preview.needsOnD20}+ on a d20`}
            </span>
          </div>
          <div style={{ fontSize: 10.5, color: C.inkFaint, marginTop: 4 }}>* your judgement, not computed</div>
        </div>
      )}
      <button style={{ ...btn, ...(busy ? { opacity: 0.5 } : {}) }} disabled={busy}
        onClick={() => onRoll({ ...q, failedSallies: Number(q.failedSallies) || 0, rollsSinceApproaches: Number(q.rollsSinceApproaches) || 0 })}>
        {busy ? 'Rolling…' : 'Roll the check'}
      </button>
    </>
  )
}

// ── main panel ──────────────────────────────────────────────────────────────────────────
export default function SiegePanel({
  isDm, canAct, myFactionId, army, strongholds = [], sieges = [], currentDay,
  onBesiege, onLift, onAssault, onSally, onTerms, onResolve, onSurrenderCheck,
  onAllocate, onSpecialists, onPatchStronghold, onSeedStrongholds, worldContext = false,
}) {
  const [allocation, setAllocation] = useState(null)
  const [specialists, setSpecialists] = useState(null)
  const [busy, setBusy] = useState(null)
  const [result, setResult] = useState(null)
  const [sackType, setSackType] = useState('heavy')
  const [inspirational, setInspirational] = useState(0)
  const [sallyObjective, setSallyObjective] = useState('wreck_works')

  const armyId = army && army.id
  const reload = useCallback(() => {
    if (!armyId) return
    getSiegeAllocation(armyId).then(setAllocation).catch(() => {})
    getSpecialists(armyId).then(setSpecialists).catch(() => {})
  }, [armyId])
  useEffect(() => { reload() }, [reload])

  // ── world context: the stronghold roster ──────────────────────────────────────────
  if (worldContext) {
    const siegeByStronghold = Object.fromEntries(sieges.filter(s => s.status === 'active').map(s => [s.stronghold_id, s]))
    if (!strongholds.length) {
      return (
        <>
          <div style={h}>Strongholds</div>
          <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 8 }}>
            No strongholds yet. Seeding reads them straight off the map — every city and every
            fort that carries a defensive value.
          </div>
          {isDm && <button style={btn} onClick={onSeedStrongholds}>Seed strongholds from the map</button>}
        </>
      )
    }
    return (
      <>
        <div style={h}>Strongholds ({strongholds.length})</div>
        {strongholds.map(s => {
          const sg = siegeByStronghold[s.id]
          return (
            <div key={s.id} style={{ ...ui.card, padding: 8, marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <b style={{ fontSize: 12.5 }}>{s.name || `${s.type === 'city' ? 'City' : 'Fort'} (${s.row},${s.col})`}</b>
                <span style={{ fontSize: 10.5, color: C.inkSoft }}>{s.type} · ({s.row},{s.col})</span>
              </div>
              <div style={{ fontSize: 11, color: C.inkSoft }} className="tnum">
                {num(s.defensive_value)} gp defences
                {s.type === 'city' ? ` · ${num(s.population)} people · ${num(s.garrison_men)} militia` : ' · no native garrison'}
              </div>
              {sg && (
                <div style={{ fontSize: 11, color: C.crimson, marginTop: 3 }}>
                  ⚔ under siege — {pct(sg.progressPct)} · {BLOCKADE_LABEL[sg.blockade]} blockade
                </div>
              )}
              {isDm && (
                <div style={{ display: 'flex', gap: 4, marginTop: 5, alignItems: 'center' }}>
                  <span style={{ fontSize: 10.5, color: C.inkFaint }}>defensive value</span>
                  <input type="number" defaultValue={s.defensive_value} style={{ ...input, marginBottom: 0, padding: '3px 5px', fontSize: 11, width: 100 }}
                    onKeyDown={(e) => { if (e.key === 'Enter') onPatchStronghold(s.id, { defensive_value: Number(e.target.value) }) }} />
                  <span style={{ fontSize: 10, color: C.inkFaint }}>⏎</span>
                </div>
              )}
            </div>
          )
        })}
      </>
    )
  }

  // ── army context ──────────────────────────────────────────────────────────────────
  if (!army) return null
  // NB: an army carries its position as tile_row/tile_col (the armies view joins the tile),
  // while a stronghold row carries row/col. Matching the wrong pair silently hides this tab.
  const here = strongholds.find(s => s.row === army.tile_row && s.col === army.tile_col)
  if (!here) {
    return <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 12 }}>No stronghold on this tile.</div>
  }
  const siege = sieges.find(s => s.stronghold_id === here.id && s.status === 'active')
  const mine = army.faction_id === myFactionId || isDm
  const iAmBesieger = siege && (siege.besieger_faction_id === army.faction_id)
  const iAmDefender = siege && here.controller_id === army.faction_id

  const act = async (key, fn) => {
    setBusy(key); setResult(null)
    try { const r = await fn(); setResult(r && r.data ? r.data : r) } finally { setBusy(null); reload() }
  }

  return (
    <>
      <div style={h}>{here.name || (here.type === 'city' ? 'City' : 'Fortress')}</div>
      <div style={{ fontSize: 11.5, color: C.inkSoft, marginBottom: 6 }} className="tnum">
        {here.type} · {num(here.defensive_value)} gp defences
        {here.type === 'city' && ` · ${num(here.population)} people`}
        {here.stored_supply_gp != null && ` · ${num(here.stored_supply_gp)} gp stores`}
      </div>

      <GarrisonCard
        strongholdType={here.type}
        garrison={siege ? siege.garrison : {
          men: here.garrison_men, fullStrength: here.garrison_men,
          troop: 'Militia Spearman', wageGp: (here.garrison_men || 0) * 0.04464,
          regenPerDay: Math.ceil((here.garrison_men || 0) * 0.02), regenPaused: false,
        }} />

      {!siege && mine && here.controller_id !== army.faction_id && (
        <>
          <div style={{ fontSize: 11.5, color: C.inkSoft, margin: '10px 0 6px' }}>
            No siege here yet. Investing the place starts the works and lets you blockade it.
          </div>
          <button style={btn} disabled={busy === 'besiege'}
            onClick={() => act('besiege', () => onBesiege(army.id))}>
            {busy === 'besiege' ? 'Investing…' : '⚔ Begin siege'}
          </button>
        </>
      )}

      {siege && (
        <>
          <div style={h}>Progress</div>
          <ProgressLadder siege={siege} />
          <div style={{ display: 'flex', gap: 10, fontSize: 11.5, marginBottom: 8 }}>
            <span><b>Blockade</b> {BLOCKADE_LABEL[siege.blockade]}</span>
            <span><b>Assault value</b> <span className="tnum">{(siege.assaultValue || 0).toFixed(3)}</span></span>
          </div>
          <div style={{ fontSize: 11, color: C.inkSoft, marginBottom: 8 }}>{BLOCKADE_EFFECT[siege.blockade]}</div>
          <MilestoneList siege={siege} />

          {siege.foothold ? (
            <div style={{ fontSize: 11.5, color: C.green, marginTop: 6 }}>
              ✓ Foothold gained — further assaults ignore the assault-value penalty.
            </div>
          ) : null}
          {siege.assault_blocked ? (
            <div style={{ fontSize: 11.5, color: C.crimson, marginTop: 6 }}>
              The troops refuse to assault again (threshold now {siege.assault_morale_threshold})
              until reinforcements arrive or a new milestone lands.
            </div>
          ) : null}
        </>
      )}

      {siege && iAmBesieger && mine && (
        <>
          <Disposition army={army} allocation={allocation} busy={busy === 'alloc'}
            onSave={(body) => act('alloc', () => onAllocate(army.id, body))} />
          <Specialists specialists={specialists} busy={busy === 'spec'}
            onSave={(d) => act('spec', () => onSpecialists(army.id, d))} />

          <div style={h}>Storm the walls</div>
          <div style={{ fontSize: 11.5, color: C.inkSoft, marginBottom: 6 }}>
            A single-front battle: your damage is multiplied by the assault value
            ({(siege.assaultValue || 0).toFixed(3)}). Win it and you are inside.
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button style={btn} disabled={busy === 'assault'}
              onClick={() => act('assault', () => onAssault(siege.id, {}))}>
              {busy === 'assault' ? 'Assaulting…' : 'Assault'}
            </button>
            {siege.hasty_assault_day == null && currentDay - siege.start_day <= 1 && (
              <button style={btnGhost} disabled={busy === 'hasty'}
                onClick={() => act('hasty', () => onAssault(siege.id, { hasty: true }))}
                title="One attempt only, the day after arriving, with up to half your men">
                Hasty assault
              </button>
            )}
          </div>

          <div style={h}>Terms offered</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {['generous', 'standard', 'harsh', 'none'].map(t => (
              <button key={t} style={siege.terms_offered === t ? btn : btnGhost}
                onClick={() => act('terms', () => onTerms(siege.id, { terms: t }))}>
                {t}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 4 }}>
            Generous +8 to their surrender roll, standard +4, none −4. Softening terms after
            offering harsher ones doubles the morale cost to your own troops.
          </div>

          <div style={h}>Take the place</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <label style={{ flex: 1, fontSize: 12 }}>Terms of its fall
              <select value={sackType} onChange={(e) => setSackType(e.target.value)} style={input}>
                <option value="generous">Generous — no loot, 0.5× garrison need</option>
                <option value="standard">Standard — 2% wealth, 5% tax cut</option>
                <option value="light">Light sack — 6% wealth, 0.5% killed</option>
                <option value="moderate">Moderate sack — 12% wealth, 2% killed</option>
                <option value="heavy">Heavy sack — 25% wealth, 6% killed</option>
              </select>
            </label>
            <label style={{ width: 76, fontSize: 12 }}>Inspir.
              <input type="number" min={0} value={inspirational}
                onChange={(e) => setInspirational(Math.max(0, Number(e.target.value) || 0))} style={input} />
            </label>
          </div>
          <div style={{ fontSize: 11, color: C.inkSoft, marginBottom: 6 }}>
            Treasury keeps {25 + 5 * inspirational}% of the loot; the rest is a windfall to the troops.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={btn} disabled={busy === 'resolve'}
              onClick={() => act('resolve', () => onResolve(siege.id, { outcome: 'stormed', sackType, inspirational }))}>
              Taken by storm
            </button>
            <button style={btnGhost} disabled={busy === 'resolve2'}
              onClick={() => act('resolve2', () => onResolve(siege.id, { outcome: 'surrendered', sackType, inspirational }))}>
              Surrendered
            </button>
          </div>

          <div style={h}>Abandon</div>
          <button style={btnGhost} disabled={busy === 'lift'}
            onClick={() => act('lift', () => onLift(siege.id))}>
            Lift the siege
          </button>
          <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 4 }}>
            March away and the defenders tear down a percentage point of works per day. A
            detachment of 20 men holds progress where it is.
          </div>
        </>
      )}

      {siege && iAmDefender && mine && (
        <>
          <div style={h}>Sally</div>
          <div style={{ fontSize: 11.5, color: C.inkSoft, marginBottom: 6 }}>
            Your field army strikes out at the works. The native militia cannot sally — only
            regulars go out of the gate.
          </div>
          <select value={sallyObjective} onChange={(e) => setSallyObjective(e.target.value)} style={input}>
            <option value="wreck_works">Destroy works (−1.5 percentage points)</option>
            <option value="burn_supplies">Burn the besiegers' supplies</option>
            <option value="kill_engineers">Kill or capture engineers (1d4)</option>
            <option value="kill_casters">Kill or capture caster laborers</option>
            <option value="break_blockade">Break the blockade (let supplies in)</option>
            <option value="escape">Escape into the field</option>
          </select>
          <button style={btn} disabled={busy === 'sally'}
            onClick={() => act('sally', () => onSally(siege.id, { objective: sallyObjective }))}>
            {busy === 'sally' ? 'Sallying…' : 'Sally forth'}
          </button>
        </>
      )}

      {siege && isDm && (
        <SurrenderCheck siege={siege} busy={busy === 'surrender'}
          onRoll={(q) => act('surrender', () => onSurrenderCheck(siege.id, q))} />
      )}

      {result && (
        <div style={{ ...ui.card, padding: 9, marginTop: 12, background: C.paperSunken }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <b style={{ fontSize: 12 }}>Result</b>
            <button style={{ ...btnGhost, padding: '1px 7px', fontSize: 11 }} onClick={() => setResult(null)}>×</button>
          </div>
          {result.error
            ? <div style={{ fontSize: 11.5, color: C.crimson, marginTop: 4 }}>{result.error}</div>
            : <ResultBody r={result} />}
        </div>
      )}
    </>
  )
}

// Render whichever action came back, in the language of the thing that happened.
function ResultBody({ r }) {
  const line = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, padding: '1px 0' }}>
      <span style={{ color: C.inkSoft }}>{label}</span><span className="tnum">{value}</span>
    </div>
  )
  if (r.attackerDamage != null) {   // an assault
    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: r.foothold ? C.green : C.crimson, marginBottom: 3 }}>
          {r.foothold ? 'Foothold gained — you are inside the walls' : 'Repulsed from the walls'}
        </div>
        {line('Your damage', num(r.attackerDamage, 2))}
        {line('Their damage', num(r.defenderDamage, 2))}
        {line('Assault value', r.assaultValue)}
        {r.garrison && line('Militia on the walls', `${num(r.garrison.men)} (${num(r.garrison.wageGp, 2)} gp)`)}
        {r.garrisonNote && <div style={{ fontSize: 11, color: C.inkFaint, marginTop: 3 }}>{r.garrisonNote}</div>}
        {r.hastyInfo && line('Caught unaware', `${Math.round(r.hastyInfo.caughtUnawarePct * 100)}%`)}
        {r.moraleCheck && line('Morale check', `${r.moraleCheck.roll} vs ${r.moraleCheck.threshold} — ${r.moraleCheck.passed ? 'holds' : 'refuses'}`)}
        <div style={{ fontSize: 11, marginTop: 5, color: C.inkSoft }}>
          {(r.defenderLosses || []).map((l, i) => (
            <div key={i}>{l.garrison ? `Militia: ${num(l.lost)} lost, ${num(l.remaining)} left` : `${l.troop}: ${num(l.dead)} dead, ${num(l.incapacitated)} down`}</div>
          ))}
          {(r.attackerLosses || []).map((l, i) => (
            <div key={i} style={{ color: C.crimson }}>Yours — {l.troop}: {num(l.dead)} dead, {num(l.incapacitated)} down</div>
          ))}
        </div>
      </div>
    )
  }
  if (r.surrendered != null) {      // a surrender check
    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: r.surrendered ? C.green : C.inkSoft }}>
          {r.surrendered ? 'The city surrenders' : 'They hold out'}
        </div>
        {line('Roll', `${r.roll} ${r.modifiers >= 0 ? '+' : ''}${r.modifiers} = ${r.total}`)}
        {line('Needed', r.target)}
      </div>
    )
  }
  if (r.objective) {                // a sally
    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: r.success ? C.green : C.crimson }}>
          {r.success ? 'The sally succeeds' : 'The sally is thrown back'}
        </div>
        {line('Sortie damage', num(r.sallyDamage, 2))}
        {line('Guards', `${num(r.guardsPresent)} men (${r.guardRole.replace('_', ' ')})`)}
        {line('Losses', `${num(r.sallyLosses)} yours / ${num(r.guardLosses)} theirs`)}
        {(r.effects || []).map((e, i) => <div key={i} style={{ fontSize: 11.5, marginTop: 3 }}>{e.effect}</div>)}
      </div>
    )
  }
  if (r.outcome) {                  // the place fell
    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.green }}>{r.stronghold} is yours ({r.sackType})</div>
        {line('Wealth extracted', `${num(r.wealthExtracted)} gp`)}
        {line('To your treasury', `${num(r.toTreasury)} gp (${r.treasurySharePct}%)`)}
        {line('To the troops', `${num(r.toTroops)} gp`)}
        {line('Population killed', num(r.populationKilled))}
        {line('Tax revenue cut', `${Math.round(r.taxRevenueCut * 100)}%`)}
        {r.occupierMorale !== 'none' && <div style={{ fontSize: 11, marginTop: 4, color: C.inkSoft }}>Occupier morale: {r.occupierMorale}</div>}
      </div>
    )
  }
  if (r.terms) return <div style={{ fontSize: 11.5, marginTop: 4 }}>Terms now <b>{r.terms}</b>{r.warning ? ` — ${r.warning}` : ''}</div>
  if (r.siegeId) return <div style={{ fontSize: 11.5, marginTop: 4 }}>Done.</div>
  return <div style={{ fontSize: 11.5, marginTop: 4 }}>Done.</div>
}
